import { useMemo } from 'react'
import { resolveDownloadUrl } from '@/api/endpoints/youtubeDownload'
import type { VideoMetadata } from '../hooks/useClipPlayback'
import { videosNeedingProbe } from '../timeline'
import type { ProjectVideo } from '../model'
import styles from '../styles/editor.module.scss'

export interface DurationProbesProps {
  videos: ProjectVideo[]
  onMetadata: (videoId: string, info: VideoMetadata) => void
}

/**
 * Off-screen ``<video>`` elements for sources the server never described.
 *
 * A video added from "já no servidor" arrives with ``duration: 0`` and no
 * dimensions — the imports endpoint doesn't probe — and both matter: the
 * concatenated timeline can't place a stretch it can't measure, and the
 * stage can't give the frame its real shape without an aspect. That second
 * one is not cosmetic: captions are fractions of the frame, so a box with
 * the wrong shape puts them somewhere the export won't.
 *
 * ``preload="metadata"`` fetches the ``moov`` atom and nothing else, so this
 * costs a few hundred KB per missing video.
 */
export function DurationProbes({ videos, onMetadata }: DurationProbesProps) {
  const pending = useMemo(() => videosNeedingProbe(videos), [videos])

  return (
    <>
      {pending.map((v) => (
        <video
          key={v.id}
          className={styles.probe}
          // Proxy when there is one: this only needs the moov, and the
          // proxy's is a few hundred KB against the master's gigabytes.
          src={resolveDownloadUrl(v.proxyUrl ?? v.videoUrl)}
          preload="metadata"
          muted
          onLoadedMetadata={(e) => {
            const el = e.currentTarget
            if (!isFinite(el.duration) || el.duration <= 0) return
            onMetadata(v.id, {
              duration: el.duration,
              width: el.videoWidth,
              height: el.videoHeight,
            })
          }}
        />
      ))}
    </>
  )
}
